import { useCallback, useContext, useEffect, useReducer, useState } from "react";
import { toast } from "react-toastify";
import { isArray } from "lodash";
import { SocketContext } from "../../../context/Socket/SocketContext";
import toastError from "../../../errors/toastError";
import {
  listCampaigns,
  getCampaign,
  deleteCampaign,
  cancelCampaign,
  stopCampaign,
  restartCampaign as restartCampaignApi,
} from "../../../services/campaigns.api";
import { i18n } from "../../../translate/i18n";

const reducer = (state, action) => {
  if (action.type === "LOAD_CAMPAIGNS") {
    const campaigns = action.payload;
    // ✅ REEMPLAZAR completamente las campañas (no agregar)
    // Esto asegura que cuando cambia la página o búsqueda, se muestren los datos correctos
    if (isArray(campaigns)) {
      return campaigns;
    }
    return [];
  }

  if (action.type === "UPDATE_CAMPAIGNS") {
    const campaign = action.payload;
    // ✅ CORRECCIÓN DE RAÍZ: Validar que el payload existe y tiene id antes de procesar
    if (!campaign || !campaign.id) {
      console.warn("[Campaigns] UPDATE_CAMPAIGNS: Payload inválido recibido:", campaign);
      return state;
    }
    const campaignIndex = state.findIndex((u) => u && u.id === campaign.id);
    if (campaignIndex !== -1) {
      // ✅ Crear nueva referencia del array para forzar re-render
      const updatedState = [...state];
      updatedState[campaignIndex] = { ...campaign };
      return updatedState;
    } else {
      // ✅ Agregar nueva campaña al inicio
      return [{ ...campaign }, ...state];
    }
  }

  if (action.type === "DELETE_CAMPAIGN") {
    const campaignId = action.payload;
    const campaignIndex = state.findIndex((u) => u.id === campaignId);
    if (campaignIndex !== -1) {
      state.splice(campaignIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useCampaignsPage = () => {
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [campaigns, dispatch] = useReducer(reducer, []);
  const [campaignToPause, setCampaignToPause] = useState(null);
  const [campaignToStop, setCampaignToStop] = useState(null);
  const [pauseModalOpen, setPauseModalOpen] = useState(false);
  const [stopModalOpen, setStopModalOpen] = useState(false);
  const [deletingCampaign, setDeletingCampaign] = useState(null);

  const socketManager = useContext(SocketContext);

  // ✅ fetchCampaigns envuelto en useCallback para estabilidad de referencia
  const fetchCampaigns = useCallback(async () => {
    try {
      // ✅ Cargar TODAS las campañas de una vez (pageNumber = 1 con limit grande en backend)
      const { data } = await listCampaigns({ searchParam, pageNumber: 1 }); // Siempre página 1, el backend devuelve todas con limit=1000
      // Las campañas con plantilla (Meta) tienen su propia página (doc 0452 §9.6).
      dispatch({ type: "LOAD_CAMPAIGNS", payload: data.records.filter((c) => !c.templateId) });
      setHasMore(false); // Ya no hay más porque cargamos todo
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  }, [searchParam]);

  // Resetear datos al cambiar búsqueda
  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  // Fetch con debounce al cambiar búsqueda
  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchCampaigns();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam]); // ✅ Eliminado pageNumber porque siempre cargamos todo de una vez

  // INVARIANTE: Socket lifecycle — cleanup idéntico al original
  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);

    socket.on(`company-${companyId}-campaign`, (data) => {
      if ((data.action === "update" || data.action === "create") && !data.record?.templateId) {
        dispatch({ type: "UPDATE_CAMPAIGNS", payload: data.record });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_CAMPAIGN", payload: +data.id });
      }
    });
    return () => {
      socket.disconnect();
    };
  }, [socketManager]);

  const handleDeleteCampaign = async (campaignId) => {
    try {
      await deleteCampaign(campaignId);
      toast.success(i18n.t("campaigns.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingCampaign(null);
    setSearchParam("");
    setPageNumber(1);
  };

  // ✅ ELIMINADO: Infinite scroll ya no es necesario porque cargamos todas las campañas de una vez
  // const loadMore = () => { ... }
  // const handleScroll = (e) => { ... }

  const handlePauseCampaign = (campaign) => {
    setCampaignToPause(campaign);
    setPauseModalOpen(true);
  };

  const confirmPauseCampaign = async () => {
    if (!campaignToPause) return;
    try {
      await cancelCampaign(campaignToPause.id);
      toast.success(i18n.t("campaigns.toasts.cancel"));
      setPageNumber(1);
      fetchCampaigns();
      setPauseModalOpen(false);
      setCampaignToPause(null);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleStopCampaign = (campaign) => {
    setCampaignToStop(campaign);
    setStopModalOpen(true);
  };

  const confirmStopCampaign = async () => {
    if (!campaignToStop) return;
    try {
      await stopCampaign(campaignToStop.id);
      toast.success("Campaña parada definitivamente");
      setPageNumber(1);
      fetchCampaigns();
      setStopModalOpen(false);
      setCampaignToStop(null);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const restartCampaign = async (campaign) => {
    try {
      await restartCampaignApi(campaign.id);
      toast.success(i18n.t("campaigns.toasts.restart"));
      setPageNumber(1);
      fetchCampaigns();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const copyCampaign = async (campaign, onSuccess) => {
    try {
      const { data } = await getCampaign(campaign.id);
      const copiedValues = {
        name: `${data.name} - Copia`,
        message1: data.message1 || "",
        message2: data.message2 || "",
        message3: data.message3 || "",
        message4: data.message4 || "",
        message5: data.message5 || "",
        confirmationMessage1: data.confirmationMessage1 || "",
        confirmationMessage2: data.confirmationMessage2 || "",
        confirmationMessage3: data.confirmationMessage3 || "",
        confirmationMessage4: data.confirmationMessage4 || "",
        confirmationMessage5: data.confirmationMessage5 || "",
        confirmation: data.confirmation || false,
        whatsappId: data.whatsappId || "",
        contactListId: data.contactListId || "",
        tagListId: data.tagListId || "",
        tipo: data.tipo || "UNICA",
        isRecurring: data.isRecurring || false,
        recurrenceType: data.recurrenceType || "",
        recurrenceInterval: data.recurrenceInterval || "",
        recurrenceDaysOfWeek: data.recurrenceDaysOfWeek || "",
        recurrenceDayOfMonth: data.recurrenceDayOfMonth || "",
        recurrenceEndDate: "",
        maxExecutions: data.maxExecutions || "",
        messageInterval: data.messageInterval || 45,
        longerIntervalAfter: data.longerIntervalAfter || 20,
        greaterInterval: data.greaterInterval || 60,
        autoTagEnabled: data.autoTagEnabled || false,
        autoTagId: data.autoTagId || null,
      };
      toast.success("Campaña copiada. Revise y ajuste el nombre antes de crear.");
      onSuccess(copiedValues);
    } catch (err) {
      toastError(err);
    }
  };

  return {
    campaigns,
    loading,
    searchParam,
    setSearchParam,
    deletingCampaign,
    setDeletingCampaign,
    pauseModalOpen,
    setPauseModalOpen,
    campaignToPause,
    setCampaignToPause,
    stopModalOpen,
    setStopModalOpen,
    campaignToStop,
    setCampaignToStop,
    fetchCampaigns,
    handleDeleteCampaign,
    handlePauseCampaign,
    confirmPauseCampaign,
    handleStopCampaign,
    confirmStopCampaign,
    restartCampaign,
    copyCampaign,
  };
};

export default useCampaignsPage;
